import { Check, CircleAlert, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils/cn";
import type { SaveStatus as Status } from "@/lib/hooks/useAutosave";

interface SaveStatusProps {
  status: Status;
  className?: string;
}

/**
 * Visible autosave status — the fix for today's silent saves. Announced via
 * aria-live="polite" so screen-reader users hear "Saving…/Saved/Couldn't save".
 */
export function SaveStatus({ status, className }: SaveStatusProps) {
  const { t } = useTranslation();

  if (status === "idle") {
    return <span aria-live="polite" className="sr-only" />;
  }

  const config = {
    saving: {
      icon: <Loader2 className="size-4 animate-spin" aria-hidden />,
      label: t("autosave.saving"),
      cls: "text-muted-foreground",
    },
    saved: {
      icon: <Check className="size-4" aria-hidden />,
      label: t("autosave.saved"),
      cls: "text-success",
    },
    error: {
      icon: <CircleAlert className="size-4" aria-hidden />,
      label: t("autosave.error"),
      cls: "text-destructive",
    },
  }[status];

  return (
    <span
      aria-live="polite"
      role="status"
      className={cn("inline-flex items-center gap-1.5 text-sm", config.cls, className)}
    >
      {config.icon}
      {config.label}
    </span>
  );
}
